import DataTableCompanyEmployees from "@/components/data-table-company-employees/data-table-company-employees";
import { CompanyErrorMessage } from "@/components/company/company-run-alerts";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useEmployeeSearch } from "@/hooks/useEmployeeSearch";
import { useTranslation } from "@/hooks/useTranslation";
import { CompanyDTO } from "@shaarp/common";
import {
  BadgeInfoIcon,
  ExternalLinkIcon,
  Loader2Icon,
  SearchIcon,
  SparklesIcon,
} from "lucide-react";
import { RiLinkedinFill } from "react-icons/ri";
import { Link } from "react-router-dom";

export default function CompanyEmployeesContent({
  company,
}: {
  company: CompanyDTO;
}) {
  const { t } = useTranslation();

  const search = useEmployeeSearch({
    company,
    smallCompanyDetection: { mode: "range", value: "1-10" },
  });

  if (search.error) {
    return <CompanyErrorMessage error={search.error} />;
  }

  if (search.isSmallCompany) {
    return (
      <div className="flex flex-col gap-4">
        <DataTableCompanyEmployees
          employees={search.employees}
          companyId={company.id}
          isLoading={search.isSearching || search.isSearchQueryLoading}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {t("company.employees.searchQuery.title")}
          </CardTitle>
          <CardDescription>
            {t("company.employees.searchQuery.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {search.isSearchQueryLoading ? (
            <Skeleton className="w-full h-10" />
          ) : (
            <>
              <div className="flex gap-2">
                <TooltipProvider>
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={search.handleGenerateQuery}
                        disabled={
                          search.isGeneratingQuery ||
                          search.isSearching ||
                          search.isSearchQueryLoading
                        }
                      >
                        {search.isGeneratingQuery ? (
                          <Loader2Icon className="w-4 h-4 animate-spin" />
                        ) : (
                          <SparklesIcon className="w-4 h-4" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{t("company.employees.searchQuery.generateTarget")}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <Input
                  value={search.searchQuery}
                  onChange={(e) => search.setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (
                      e.key === "Enter" &&
                      !e.shiftKey &&
                      !search.isButtonDisabled()
                    ) {
                      e.preventDefault();
                      search.handleButtonClick();
                    }
                  }}
                  placeholder={t("company.employees.searchQuery.placeholder")}
                  className={
                    search.hasSearched && !search.queryHasChanged
                      ? "flex-1"
                      : "flex-1 border border-warning rounded-md transition-all duration-300"
                  }
                />

                <TooltipProvider>
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <span className="inline-block">
                        <Button
                          onClick={search.handleButtonClick}
                          disabled={search.isButtonDisabled()}
                          className="pointer-events-auto"
                        >
                          {search.isSearching ? (
                            <Loader2Icon className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <SearchIcon className="w-4 h-4 mr-2" />
                          )}
                          {search.getButtonText()}
                        </Button>
                      </span>
                    </TooltipTrigger>
                    {search.getDisabledTooltip() && (
                      <TooltipContent>
                        <p>{search.getDisabledTooltip()}</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {!search.hasSearched && (
        <div className="flex flex-col items-center justify-center py-12 text-center text-muted-foreground">
          <p className="text-sm">
            {t("company.employees.searchQuery.emptyState.description")}
          </p>
        </div>
      )}

      {search.hasSearched && (search.isSearching || search.employees.length > 0) && (
        <>
          <Alert className="flex items-center justify-between gap-4 [&>svg]:translate-y-0 [&>svg]:size-5">
            <BadgeInfoIcon className="shrink-0" />
            <p className="flex-1 text-sm">
              <span className={search.targetMatch === "perfect" ? "" : "underline"}>
                {(() => {
                  switch (search.targetMatch ?? "other") {
                    case "perfect":
                      return t(
                        "company.employees.notAllEmployeesAreShown.perfect",
                      );
                    case "management":
                      return t(
                        "company.employees.notAllEmployeesAreShown.management",
                      );
                    case "other":
                    default:
                      return t(
                        "company.employees.notAllEmployeesAreShown.other",
                      );
                  }
                })()}
              </span>{" "}
              {t("company.employees.notAllEmployeesAreShown.gdpr")}
            </p>
            {company.socialNetworks.linkedin.url && (
              <Button variant="default" size="sm" className="shrink-0" asChild>
                <Link
                  to={`${company.socialNetworks.linkedin.url}/people`}
                  target="_blank"
                >
                  <RiLinkedinFill className="w-4 h-4" />
                  {t("company.employees.viewOnLinkedIn")}
                  <ExternalLinkIcon className="w-3.5 h-3.5" />
                </Link>
              </Button>
            )}
          </Alert>
          <DataTableCompanyEmployees
            employees={search.employees}
            companyId={company.id}
            isLoading={search.isSearching}
          />
          {search.employees.length > 5 && (
            <div className="flex flex-col items-center gap-2">
              <TooltipProvider>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <span className="inline-block">
                      <Button
                        onClick={search.handleButtonClick}
                        disabled={search.isButtonDisabled()}
                        className="pointer-events-auto"
                      >
                        {search.isSearching ? (
                          <Loader2Icon className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <SearchIcon className="w-4 h-4 mr-2" />
                        )}
                        {search.getButtonText()}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {search.getDisabledTooltip() && (
                    <TooltipContent>
                      <p>{search.getDisabledTooltip()}</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
              {search.currentPage >= 1 &&
                !search.hasMoreResults &&
                !search.queryHasChanged &&
                !search.isSearching && (
                  <p className="text-sm text-muted-foreground">
                    {t("company.employees.searchQuery.noAdditionalResults")}
                  </p>
                )}
            </div>
          )}
        </>
      )}

      {search.maxPagesReached && (
        <div className="flex flex-col items-center justify-center text-center text-muted-foreground">
          <p className="text-md font-medium">
            {t("company.employees.searchQuery.refineSearch.title")}
          </p>
          <p className="text-sm">
            {t("company.employees.searchQuery.refineSearch.description")}
          </p>
        </div>
      )}

      {search.hasSearched && !search.isSearching && search.employees.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center text-muted-foreground">
          <p className="text-lg font-medium">
            {t("company.employees.searchQuery.noResults.title")}
          </p>
          <p className="text-sm">
            {t("company.employees.searchQuery.noResults.description")}
          </p>
          <Button
            variant="outline"
            onClick={search.handleGenerateAndSearch}
            disabled={search.isGeneratingQuery || search.isSearching}
            className="mt-4"
          >
            {search.isGeneratingQuery || search.isSearching ? (
              <Loader2Icon className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <SparklesIcon className="w-4 h-4 mr-2" />
            )}
            {t("company.employees.searchQuery.searchOtherRelevantContacts")}
          </Button>
        </div>
      )}
    </div>
  );
}
